
// Main entry point for the React app
import React, { Suspense, lazy, useEffect } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './styles/design-tokens.css';
import './styles/compact-design-system.css';

import Layout from './components/Layout/Layout';
import { AuthProvider } from './components/userManagement';

// Supply Management Components
// Import individual components directly




import { AuthorizationProvider } from './components/common/Authorization';
import ProtectedRoute from './components/common/ProtectedRoute';
import PageLoader from './components/common/PageLoader';
import ThemeProvider from './contexts/ThemeContext';
import { ActiveMeetingProvider } from './components/CommunicationTunnel/ActiveMeetingProvider';
// Bootstrap CSS is now imported in index.js for consistent load order
import './App.css';
import './styles/button-overrides.css'; // MUST BE LAST - forces black text on all buttons
import { useAuth } from './components/userManagement';





const TestAddressForm = lazy(() => import('./components/ActorManagement/TestAddressForm'));
const TagManagement = lazy(() => import('./components/TagManagement/TagManagement'));
const MyWorkspacePage = lazy(() => import('./components/Workspace/MyWorkspacePage'));
const VehicleManagementRouter = lazy(() => import('./components/VehicleManagement/VehicleManagementRouter'));
const RepairProcessList = lazy(() => import('./components/RepairProcessManagement/RepairProcessList'));
const RepairProcessDetails = lazy(() => import('./components/RepairProcessManagement/RepairProcessDetails'));
const CreateRepairProcess = lazy(() => import('./components/RepairProcessManagement/CreateRepairProcess'));
const NotificationChannels = lazy(() => import('./components/notificationManagement/NotificationChannels'));
const NotificationTemplates = lazy(() => import('./components/notificationManagement/NotificationTemplates'));
const NotificationEventTypes = lazy(() => import('./components/notificationManagement/NotificationEventTypes'));
const NotificationLogs = lazy(() => import('./components/notificationManagement/NotificationLogs'));
const DocumentGeneration = lazy(() => import('./components/documentGeneration'));
const InterfacingManagementRouter = lazy(() => import('./components/interfacingManagement/InterfacingManagementRouter'));
const FinanceManagementRouter = lazy(() => import('./components/Finance/FinanceManagementRouter'));
const DriverDashboard = lazy(() => import('./pages/DriverDashboard'));
const DriverBookingDetail = lazy(() => import('./pages/DriverBookingDetail'));
const TourDashboard = lazy(() => import('./pages/TourDashboard'));
const BlockManagement = lazy(() => import('./pages/BlockManagement'));
const RoutingRules = lazy(() => import('./pages/RoutingRules'));
const PmiOperations = lazy(() => import('./pages/PmiOperations'));
const WorkTaskWorkspace = lazy(() => import('./components/WorkTasks/WorkTaskWorkspace'));
const PmiControlCenter = lazy(() => import('./pages/PmiControlCenter'));
const PmiApiConnections = lazy(() => import('./pages/PmiApiConnections'));
const HospitalityOperations = lazy(() => import('./pages/HospitalityOperations'));
const PmiRestaurantOperations = lazy(() => import('./pages/PmiRestaurantOperations'));
const PmiPlatformDashboard = lazy(() => import('./pages/PmiPlatformDashboard'));
const OperationsToday = lazy(() => import('./pages/OperationsToday'));
const OrdersFulfilment = lazy(() => import('./pages/OrdersFulfilment'));
const PmiProductStudio = lazy(() => import('./pages/PmiProductStudio'));
const PmiOverview = lazy(() => import('./pages/PmiOverview'));
const PmiSuppliersPage = lazy(() => import('./pages/PmiSuppliersPage'));
const PmiOrdersPage = lazy(() => import('./pages/PmiOrdersPage'));
const PmiPricingPage = lazy(() => import('./pages/PmiPricingPage'));
const PmiWidgetsPage = lazy(() => import('./pages/PmiWidgetsPage'));
const PmiAdminWidgetStudio = lazy(() => import('./pages/PmiAdminWidgetStudio'));
const PmiBookingPage = lazy(() => import('./pages/PmiBookingPage'));
const PmiCustomersPage = lazy(() => import('./pages/PmiCustomersPage'));
const PmiChannelsPage = lazy(() => import('./pages/PmiChannelsPage'));
const PmiAppointmentsPage = lazy(() => import('./pages/PmiAppointmentsPage'));
const PmiMarketplacePage = lazy(() => import('./pages/PmiMarketplacePage'));
const PmiRoomReservationsPage = lazy(() => import('./pages/PmiRoomReservationsPage'));
const PmiCustomerWorkspace = lazy(() => import('./pages/PmiCustomerWorkspace'));
const ExecutiveKpi = lazy(() => import('./pages/ExecutiveKpi'));
const StaffPlannerRouter = lazy(() => import('./components/StaffPlanner/StaffPlannerRouter'));
const DealerManagement = lazy(() => import('./pages/DealerManagement'));
const CustomerPortal = lazy(() => import('./pages/CustomerPortal'));
const VapiSettings = lazy(() => import('./pages/VapiSettings'));
const SurveyDashboard = lazy(() => import('./pages/SurveyDashboard'));
const PickupConfirm = lazy(() => import('./pages/PickupConfirm'));
const DriverScan = lazy(() => import('./pages/DriverScan'));
const ResetPassword = lazy(() => import('./components/userManagement/ResetPassword'));
const CustomerManagementRouter = lazy(() => import('./components/CustomerManagement/CustomerManagementRouter'));
const CustomerLogin = lazy(() => import('./components/CustomerManagement/CustomerLogin'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard/AdminDashboard'));
const UnifiedAnalyticsDashboard = lazy(() => import('./components/Dashboard/UnifiedAnalyticsDashboard'));
const BadgeAnalytics = lazy(() => import('./components/Analytics/BadgeAnalytics'));
const DriverManagement = lazy(() => import('./components/DriverManagement/DriverManagement'));
const DriverMobileApp = lazy(() => import('./components/DriverManagement/DriverMobileApp'));
const Homepage = lazy(() => import('./components/Homepage/Homepage'));
const IntelligenceDashboard = lazy(() => import('./components/Intelligence/IntelligenceDashboard'));
const DiagnosticDashboard = lazy(() => import('./components/Diagnostic/DiagnosticDashboard'));
const RepairCalendar = lazy(() => import('./components/RepairCalendar/RepairCalendar'));
const Unauthorized = lazy(() => import('./components/common/Unauthorized'));
const SparePartManagement = lazy(() => import('./components/sparePartManagement/SparePartManagement'));
const DiagnosticOfferView = lazy(() => import('./components/CustomerOffer/DiagnosticOfferView'));
const DiagnosticOfferWithSpareParts = lazy(() => import('./components/CustomerOffer/DiagnosticOfferWithSpareParts'));
const PublicUploadPage = lazy(() => import('./components/PublicUpload/PublicUploadPage'));
const SupplierPortal = lazy(() => import('./components/SupplyManagement/SupplierPortal/SupplierPortal'));
const DropOffResponsePage = lazy(() => import('./components/DropOffResponse/DropOffResponsePage'));
const AvailabilityForm = lazy(() => import('./components/CustomerAvailability/AvailabilityForm'));
const AvailabilitySuccess = lazy(() => import('./components/CustomerAvailability/AvailabilitySuccess'));
const TimeConfirmationPage = lazy(() => import('./components/TimeConfirmation/TimeConfirmationPage'));
const RepairTrackingPage = lazy(() => import('./components/CustomerTracking/RepairTrackingPage'));
const SimpleRepairTracking = lazy(() => import('./components/CustomerTracking/SimpleRepairTracking'));
const RepairFeedback = lazy(() => import('./components/CustomerPortal/RepairFeedback'));
const PublicRestaurantFeedback = lazy(() => import('./components/Pmi/restaurant/PublicRestaurantFeedback'));
const PublicRestaurantMenu = lazy(() => import('./components/Pmi/restaurant/guest/PublicMenu'));
const RepairAccessPage = lazy(() => import('./components/RepairAccess/RepairAccessPage'));
const CostLimitSetup = lazy(() => import('./components/RepairProcessManagement/CostLimitSetup'));
const CostOfferPage = lazy(() => import('./components/CustomerOffer/CostOfferPage'));
const CustomerTourAlternatives = lazy(() => import('./components/CustomerTourAlternatives/CustomerTourAlternatives'));
const CallNotePage = lazy(() => import('./components/ActorManagement/CallNotePage'));
const OrderList = lazy(() => import('./components/OrderManagement/OrderList'));
const OrderDetail = lazy(() => import('./components/OrderManagement/OrderDetail'));
const OrderEdit = lazy(() => import('./components/OrderManagement/OrderEdit'));
const OrderInvoiceList = lazy(() => import('./components/OrderManagement/OrderInvoiceList'));
const InvoiceEditPage = lazy(() => import('./components/OrderManagement/InvoiceEditPage'));

const ActorManagementHub = lazy(() => import('./components/ActorManagement/ActorManagementHub'));
// The 3D damage check loads three.js, so it stays out of the main bundle.
const VehicleDamagePage = lazy(() => import('./components/VehicleDamage/VehicleDamagePage'));
const ActorDetailPage = lazy(() => import('./components/ActorManagement/ActorDetailPage'));
const ActorCreatePage = lazy(() => import('./components/ActorManagement/ActorCreatePage'));
const SalesCRM = lazy(() => import('./components/SalesCRM/SalesCRM'));
const SalesStaffManagement = lazy(() => import('./components/SalesCRM/SalesStaffManagement'));
const Login = lazy(() => import('./components/userManagement').then(m => ({ default: m.Login })));
const PasswordChange = lazy(() => import('./components/userManagement').then(m => ({ default: m.PasswordChange })));
const UserManagement = lazy(() => import('./components/userManagement').then(m => ({ default: m.UserManagement })));
const UserProfile = lazy(() => import('./components/userManagement').then(m => ({ default: m.UserProfile })));
const UserPermissions = lazy(() => import('./components/userManagement').then(m => ({ default: m.UserPermissions })));
const SupplyManagementRouter = lazy(() => import('./components/SupplyManagement').then(m => ({ default: m.SupplyManagementRouter })));
const MapManagementRouter = lazy(() => import('./components/MapManagement').then(m => ({ default: m.MapManagementRouter })));
const LMSAcademyDashboard = lazy(() => import('./components/LMSAcademy').then(m => ({ default: m.LMSAcademyDashboard })));
const WarrantyApprovalView = lazy(() => import('./components/ProducerWarranty').then(m => ({ default: m.WarrantyApprovalView })));
const CommunicationHub = lazy(() => import('./components/CommunicationTunnel').then(m => ({ default: m.CommunicationHub })));
const ConnectGuestMeetingEntry = lazy(() => import('./components/CommunicationTunnel/ConnectGuestMeetingEntry'));
const UsedScooterList = lazy(() => import('./components/UsedScooterManagement').then(m => ({ default: m.UsedScooterList })));
const CreateUsedScooterIntake = lazy(() => import('./components/UsedScooterManagement').then(m => ({ default: m.CreateUsedScooterIntake })));
const UsedScooterDetails = lazy(() => import('./components/UsedScooterManagement').then(m => ({ default: m.UsedScooterDetails })));
const AdminServiceRequestApproval = lazy(() => import('./components/ServiceRequestManagement').then(m => ({ default: m.AdminServiceRequestApproval })));
const BookingDraftReminders = lazy(() => import('./components/ServiceRequestManagement').then(m => ({ default: m.BookingDraftReminders })));
const BugReportManagement = lazy(() => import('./components/BugReportManagement'));

const App = () => {
  return (
    <ThemeProvider>
      {/* Navigations run as React transitions: the current page stays on screen while the next one loads. */}
      <Router future={{ v7_startTransition: true }}>
        {/* Above every route: a running meeting survives going to any part of the ERP. */}
        <ActiveMeetingProvider>
        <Suspense fallback={<div className="d-flex justify-content-center p-5"><div className="spinner-border text-success" role="status"></div></div>}>
        <Routes>
          {/* Public routes - these are accessible without authentication */}
          <Route path="/diagnostic-offer/:processId" element={<DiagnosticOfferView />} />
          <Route path="/diagnostic-offer/:processId/spare-parts" element={<DiagnosticOfferWithSpareParts />} />
          <Route path="/warranty-approval/:processId/:producerId" element={<WarrantyApprovalView />} />
          <Route path="/public-upload" element={<PublicUploadPage />} />
          <Route path="/public-upload/:processId" element={<PublicUploadPage />} />
          <Route path="/dropoff-response" element={<DropOffResponsePage />} />
          <Route path="/availability/:repairProcessId/:token" element={<AvailabilityForm />} />
          <Route path="/availability-success" element={<AvailabilitySuccess />} />
          <Route path="/confirm-time/:id/:token/:action" element={<TimeConfirmationPage />} />
          <Route path="/repair-access" element={<RepairAccessPage />} />
          <Route path="/cost-limit/:token" element={<CostLimitSetup />} />
          <Route path="/cost-offer/:token" element={<CostOfferPage />} />
          <Route path="/cost-offer/:token/accept" element={<CostOfferPage />} />
          <Route path="/cost-offer/:token/decline" element={<CostOfferPage />} />
          <Route path="/customer-availability/:processId" element={<CustomerTourAlternatives />} />
          {/* Supplier self-onboarding portal - hidden path, no auth required */}
          <Route path="/sp/:token" element={<SupplierPortal />} />
          <Route path="/repair-tracking/:token" element={<SimpleRepairTracking />} />
          <Route path="/repair-tracking-full/:token" element={<RepairTrackingPage />} />
          <Route path="/feedback" element={<RepairFeedback />} />
          <Route path="/feedback/booking/:bookingId" element={<RepairFeedback />} />
          <Route path="/feedback/repair/:repairProcessId" element={<RepairFeedback />} />
          <Route path="/feedback/order/:repairProcessId" element={<RepairFeedback />} />
          <Route path="/restaurant-feedback" element={<PublicRestaurantFeedback />} />
          <Route path="/menu/:slug" element={<PublicRestaurantMenu />} />
          <Route path="/meetings/:meetingId" element={<ConnectGuestMeetingEntry />} />
          <Route path="/meeting-guest/:meetingId" element={<ConnectGuestMeetingEntry />} />
          <Route path="/pickup/:repairId" element={<AuthProvider><PickupConfirm /></AuthProvider>} />
          <Route path="/driver-scan/:repairId" element={<AuthProvider><DriverScan /></AuthProvider>} />
          <Route path="/driver-app" element={<AuthProvider><DriverMobileApp /></AuthProvider>} />
          <Route path="/driver-app/:driverId" element={<AuthProvider><DriverMobileApp /></AuthProvider>} />
          <Route path="/login" element={<AuthProvider><Login /></AuthProvider>} />
          <Route path="/customer/login" element={<AuthProvider><CustomerLogin /></AuthProvider>} />
          <Route path="/customer/pmi/*" element={
            <AuthProvider>
              <AuthorizationProvider>
                <ProtectedRoute redirectTo="/unauthorized" loginPath="/login">
                  <PmiCustomerWorkspace />
                </ProtectedRoute>
              </AuthorizationProvider>
            </AuthProvider>
          } />
          <Route path="/reset-password" element={<AuthProvider><ResetPassword /></AuthProvider>} />
          <Route path="/change-password" element={<AuthProvider><PasswordChange /></AuthProvider>} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* White-label reseller customer portal (branded by dealer domain or ?brand=CODE) */}
          <Route path="/portal" element={<CustomerPortal />} />

          {/* Call Note - standalone popup (no sidebar/layout) */}
          <Route path="/call-note" element={
            <AuthProvider>
              <AuthorizationProvider>
                <CallNotePage />
              </AuthorizationProvider>
            </AuthProvider>
          } />

          {/* All other routes require authentication */}
          <Route path="/*" element={
            <AuthProvider>
              <AuthorizationProvider>
                <Layout>
                  {/* Own boundary so the sidebar and header never disappear while a page loads. */}
                  <Suspense fallback={<PageLoader />}>
                    <PmiPrefetch />
                    <AppRoutes />
                  </Suspense>
                </Layout>
              </AuthorizationProvider>
            </AuthProvider>
          } />
        </Routes>
        </Suspense>
      </ActiveMeetingProvider>
      </Router>
    </ThemeProvider>
  );
};

// PMI screens are opened one after another; once someone works in PMI, load the other PMI
// screens in the background so switching between them is instant.
const PMI_PAGE_LOADERS = [
  () => import('./pages/PmiOverview'), () => import('./pages/PmiProductStudio'),
  () => import('./pages/HospitalityOperations'), () => import('./pages/PmiRestaurantOperations'),
  () => import('./pages/PmiControlCenter'), () => import('./pages/PmiCustomersPage'),
  () => import('./pages/PmiWidgetsPage'), () => import('./pages/PmiOperations'),
  () => import('./pages/PmiApiConnections'), () => import('./pages/PmiAdminWidgetStudio'),
  () => import('./pages/PmiOrdersPage'),
];
let pmiPrefetched = false;
// Old actor links (/actors?actor_id=...) open the full actor page; the plain
// list keeps its search/filter query parameters.
const ActorsEntry = () => {
  const { search } = useLocation();
  const actorId = new URLSearchParams(search).get('actor_id');
  if (actorId) return <Navigate to={`/actors/${encodeURIComponent(actorId)}`} replace />;
  return <ActorManagementHub section="actors" />;
};

const PmiPrefetch = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pmiPrefetched || !pathname.startsWith('/admin/pmi-platform')) return undefined;
    const idle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 1500));
    const handle = idle(() => {
      pmiPrefetched = true;
      PMI_PAGE_LOADERS.reduce((chain, load) => chain.then(() => load().catch(() => null)), Promise.resolve());
    });
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(handle) : window.clearTimeout(handle));
  }, [pathname]);
  return null;
};

// Separate component for all authenticated routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Protected routes inside layout */}
      <Route path="/" element={
        <RequireAuth>
          <Homepage />
        </RequireAuth>
      } />

      <Route path="/profile" element={
        <ProtectedRoute redirectTo="/unauthorized">
          <UserProfile />
        </ProtectedRoute>
      } />

      <Route path="/permissions" element={
        <ProtectedRoute redirectTo="/unauthorized">
          <UserPermissions />
        </ProtectedRoute>
      } />

      <Route path="/repair-processes" element={
        <ProtectedRoute permissions={["view_repair_process", "view_assigned_repair_process", "edit_repair_process", "create_repair_process"]} redirectTo="/unauthorized">
          <RepairProcessList />
        </ProtectedRoute>
      } />

      <Route path="/repair-processes/:id" element={
        <ProtectedRoute permissions={["view_repair_process", "view_assigned_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <RepairProcessDetails />
        </ProtectedRoute>
      } />

      <Route path="/repair-processes/new" element={
        <ProtectedRoute permissions={["create_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <CreateRepairProcess />
        </ProtectedRoute>
      } />

      {/* Used E-Scooter Routes */}
      <Route path="/used-scooters" element={
        <ProtectedRoute permissions={["view_repair_process", "view_assigned_repair_process", "edit_repair_process", "create_repair_process"]} redirectTo="/unauthorized">
          <UsedScooterList />
        </ProtectedRoute>
      } />
      <Route path="/used-scooters/new" element={
        <ProtectedRoute permissions={["create_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <CreateUsedScooterIntake />
        </ProtectedRoute>
      } />
      <Route path="/used-scooters/:id" element={
        <ProtectedRoute permissions={["view_repair_process", "view_assigned_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <UsedScooterDetails />
        </ProtectedRoute>
      } />

      {/* Everybody's own private folder. Signing in is the only requirement:
          a personal workspace is not a right that is granted, and the server
          answers only with the files of whoever is asking. */}
      <Route path="/my-workspace" element={
        <ProtectedRoute>
          <MyWorkspacePage />
        </ProtectedRoute>
      } />

      <Route path="/actors" element={
        <ProtectedRoute permissions={["view_actors", "edit_actors", "create_actors"]} redirectTo="/unauthorized">
          <ActorsEntry />
        </ProtectedRoute>
      } />

      {/* Actor Management hub tabs: tags and workshops managed next to the actors */}
      <Route path="/actors/tags" element={
        <ProtectedRoute permissions={["view_tags", "manage_tags"]} redirectTo="/unauthorized">
          <ActorManagementHub section="tags" />
        </ProtectedRoute>
      } />

      <Route path="/actors/awards" element={
        <ProtectedRoute permissions={["view_actors", "edit_actors", "create_actors"]} redirectTo="/unauthorized">
          <ActorManagementHub section="awards" />
        </ProtectedRoute>
      } />

      <Route path="/actors/workshops" element={
        <ProtectedRoute permissions={["view_dashboard", "view_analytics", "view_actors"]} redirectTo="/unauthorized">
          <ActorManagementHub section="workshops" />
        </ProtectedRoute>
      } />

      <Route path="/repair-damage-check" element={
        <ProtectedRoute permissions={["view_repair_process", "view_assigned_repair_process", "create_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <VehicleDamagePage />
        </ProtectedRoute>
      } />

      <Route path="/actors/mechanics" element={
        <ProtectedRoute permissions={["view_dashboard", "view_analytics", "view_actors"]} redirectTo="/unauthorized">
          <ActorManagementHub section="mechanics" />
        </ProtectedRoute>
      } />

      {/* Who may do what, where, until when (ADR-0034) */}
      <Route path="/actors/access" element={
        <ProtectedRoute permissions={["manage_tags", "admin_access"]} redirectTo="/unauthorized">
          <ActorManagementHub section="access" />
        </ProtectedRoute>
      } />

      {/* Add an actor: choose Individual or Enterprise, then the full form */}
      <Route path="/actors/new" element={
        <ProtectedRoute permissions={["create_actors", "edit_actors"]} redirectTo="/unauthorized">
          <ActorCreatePage />
        </ProtectedRoute>
      } />

      {/* Full actor page: profile, contacts, tags, workshop, communication, repairs */}
      <Route path="/actors/:id" element={
        <ProtectedRoute permissions={["view_actors", "edit_actors", "create_actors"]} redirectTo="/unauthorized">
          <ActorDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/sales-crm-preview" element={
        <ProtectedRoute permissions={["view_actors", "edit_actors", "create_actors"]} redirectTo="/unauthorized">
          <SalesCRM />
        </ProtectedRoute>
      } />

      <Route path="/sales/staff" element={
        <ProtectedRoute permissions={["manage_users", "edit_users", "create_users"]} redirectTo="/unauthorized">
          <SalesStaffManagement />
        </ProtectedRoute>
      } />
      <Route path="/sales/*" element={
        <ProtectedRoute permissions={["view_actors", "view_sales"]} redirectTo="/unauthorized">
          <SalesCRM />
        </ProtectedRoute>
      } />


      <Route path="/vehicles" element={
        <ProtectedRoute permissions={["view_vehicles", "edit_vehicles", "create_vehicles"]} redirectTo="/unauthorized">
          <Navigate to="/vehicles/prototypes" replace />
        </ProtectedRoute>
      } />

      <Route path="/vehicles/*" element={
        <ProtectedRoute permissions={["view_vehicles", "edit_vehicles", "create_vehicles"]} redirectTo="/unauthorized">
          <VehicleManagementRouter />
        </ProtectedRoute>
      } />

      <Route path="/spare-parts/*" element={
        <ProtectedRoute permissions={["view_spare_parts", "edit_spare_parts", "create_spare_parts"]} roles={["Admin", "Technician", "Producer", "SparePart"]} redirectTo="/unauthorized">
          <SparePartManagement />
        </ProtectedRoute>
      } />

      <Route path="/tags" element={
        <ProtectedRoute permissions={["view_tags", "manage_tags"]} redirectTo="/unauthorized">
          <TagManagement />
        </ProtectedRoute>
      } />

      {/* Document Generation Routes */}
      <Route path="/documents" element={
        <ProtectedRoute permissions={["view_documents", "create_documents"]} redirectTo="/unauthorized">
          <DocumentGeneration />
        </ProtectedRoute>
      } />

      <Route path="/documents/generate" element={
        <ProtectedRoute permissions={["create_documents", "view_documents"]} redirectTo="/unauthorized">
          <DocumentGeneration />
        </ProtectedRoute>
      } />

      <Route path="/documents/history" element={
        <ProtectedRoute permissions={["view_documents"]} redirectTo="/unauthorized">
          <DocumentGeneration />
        </ProtectedRoute>
      } />

      {/* Test route for address functionality */}
      <Route path="/test-address" element={
        <ProtectedRoute permissions={["view_actors"]} redirectTo="/unauthorized">
          <TestAddressForm />
        </ProtectedRoute>
      } />

      {/* Interfacing Management routes */}
      <Route path="/interfacing/*" element={
        <ProtectedRoute permissions={["manage_settings"]} redirectTo="/unauthorized">
          <InterfacingManagementRouter />
        </ProtectedRoute>
      } />

      {/* Map Management routes */}
      <Route path="/map-management/*" element={
        <ProtectedRoute permissions={["view_map", "manage_routes", "view_logistics"]} redirectTo="/unauthorized">
          <MapManagementRouter />
        </ProtectedRoute>
      } />

      {/* Admin Dashboard routes */}
      <Route path="/admin-dashboard" element={
        <ProtectedRoute permissions={["view_dashboard", "view_analytics"]} redirectTo="/unauthorized">
          <AdminDashboard />
        </ProtectedRoute>
      } />

      {/* Unified Analytics & KPIs Dashboard */}
      <Route path="/repair-analytics" element={
        <ProtectedRoute permissions={["view_analytics", "view_repair_process", "view_assigned_repair_process"]} redirectTo="/unauthorized">
          <UnifiedAnalyticsDashboard />
        </ProtectedRoute>
      } />

      {/* Badges and recognition across the team */}
      <Route path="/badge-analytics" element={
        <ProtectedRoute permissions={["view_analytics", "view_dashboard", "view_actors"]} redirectTo="/unauthorized">
          <BadgeAnalytics />
        </ProtectedRoute>
      } />

      {/* Redirect old executive-kpi URL to the unified page */}
      <Route path="/executive-kpi" element={
        <ProtectedRoute permissions={["view_analytics", "view_dashboard"]} redirectTo="/unauthorized">
          <Navigate to="/repair-analytics" replace />
        </ProtectedRoute>
      } />

      {/* Customer Management routes — guests are sent to the customer login */}
      <Route path="/customer/*" element={
        <ProtectedRoute permissions={["view_actors"]} roles={["Customer", "customer"]} redirectTo="/unauthorized" loginPath="/customer/login">
          <CustomerManagementRouter />
        </ProtectedRoute>
      } />

      {/* Driver Management Routes */}
      <Route path="/driver/dashboard" element={
        <ProtectedRoute permissions={["view_logistics", "manage_logistics"]} roles={["Driver", "driver"]} redirectTo="/unauthorized">
          <DriverDashboard />
        </ProtectedRoute>
      } />

      <Route path="/driver/bookings/:bookingId" element={
        <ProtectedRoute permissions={["view_logistics", "manage_logistics"]} roles={["Driver", "driver"]} redirectTo="/unauthorized">
          <DriverBookingDetail />
        </ProtectedRoute>
      } />

      {/* Driver Management (Admin) - Logistics Routes */}
      <Route path="/driver-management" element={
        <ProtectedRoute permissions={["view_logistics_tours"]} redirectTo="/unauthorized">
          <DriverManagement />
        </ProtectedRoute>
      } />

      {/* Tour Dashboard - CEO-approved logistics tour view */}
      <Route path="/admin/tour-dashboard" element={
        <ProtectedRoute permissions={["view_logistics_tours"]} redirectTo="/unauthorized">
          <TourDashboard />
        </ProtectedRoute>
      } />

      {/* Block Management - manage tasks blocked from tours */}
      <Route path="/admin/block-management" element={
        <ProtectedRoute permissions={["view_logistics_tours"]} redirectTo="/unauthorized">
          <BlockManagement />
        </ProtectedRoute>
      } />

      {/* Auto-Routing Rules - assign bookings to a location by ZIP + opening hours */}
      <Route path="/admin/routing-rules" element={
        <ProtectedRoute permissions={["view_logistics_tours"]} redirectTo="/unauthorized">
          <RoutingRules />
        </ProtectedRoute>
      } />

      {/* Returns / RMA - white-label return authorizations branded per dealer */}
      {/* Returns are a tab of Orders & fulfilment */}
      <Route path="/admin/returns" element={<Navigate to="/admin/order-pipeline?tab=returns" replace />} />
      {/* VAPI Voice AI settings - configure phone-call AI credentials */}
      <Route path="/admin/vapi-settings" element={
        <ProtectedRoute permissions={["manage_settings", "view_communications", "edit_communications"]} redirectTo="/unauthorized">
          <VapiSettings />
        </ProtectedRoute>
      } />
      {/* Dealers - white-label reseller management (branding used on their documents) */}
      <Route path="/admin/dealers" element={
        <ProtectedRoute permissions={["manage_settings", "view_supply", "edit_supply"]} redirectTo="/unauthorized">
          <DealerManagement />
        </ProtectedRoute>
      } />
      {/* Staff Planner - full workforce suite (employees on actors, planner, attendance, …) */}
      <Route path="/staff/*" element={
        <ProtectedRoute permissions={["view_repair_process", "edit_repair_process", "manage_settings", "view_dashboard"]} redirectTo="/unauthorized">
          <StaffPlannerRouter />
        </ProtectedRoute>
      } />
      {/* Executive KPIs - revenue trend, YoY growth, fulfilment mix, forecast */}
      <Route path="/admin/executive-kpis" element={
        <ProtectedRoute permissions={["view_dashboard", "view_analytics", "manage_settings"]} redirectTo="/unauthorized">
          <ExecutiveKpi />
        </ProtectedRoute>
      } />
      {/* Order Pipeline / Kanban - orders across fulfilment stages with stuck flags */}
      <Route path="/admin/order-pipeline" element={
        <ProtectedRoute permissions={["view_supply", "edit_supply", "manage_settings", "view_dashboard"]} redirectTo="/unauthorized">
          <OrdersFulfilment />
        </ProtectedRoute>
      } />
      {/* Operations Control Center - unified "follow all" cockpit (orders + PMI + tours + distribution) */}
      <Route path="/admin/operations" element={
        <ProtectedRoute permissions={["view_dashboard", "view_analytics", "view_repair_process", "view_assigned_repair_process"]} redirectTo="/unauthorized">
          <OperationsToday />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/tasks" element={
        <ProtectedRoute permissions={["work_tasks.read"]} redirectTo="/unauthorized"><WorkTaskWorkspace /></ProtectedRoute>
      } />
      {/* PMI Control Center - platform admin overview: modules, access, health */}
      <Route path="/admin/pmi-platform/widget-studio/:domain" element={
        <ProtectedRoute permissions={["manage_settings", "manage_pmi", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiAdminWidgetStudio />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/control-center" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiControlCenter />
        </ProtectedRoute>
      } />
      {/* PMI Command Center - channels, products from every source, widgets, operations */}
      {/* Merged into the unified Products workspace */}
      <Route path="/admin/pmi-platform/command-center" element={<Navigate to="/admin/pmi-platform/products?view=channels" replace />} />
      {/* PMI API connections - client ID + secret keys for Shopify, marketplaces and the PMI app */}
      <Route path="/admin/pmi-platform/api-connections" element={
        <ProtectedRoute permissions={["edit_repair_process", "manage_settings"]} redirectTo="/unauthorized">
          <PmiApiConnections />
        </ProtectedRoute>
      } />
      {/* PMI - service guidelines, operation capacity and live ready-time queue */}
      <Route path="/admin/pmi-platform/operations" element={
        <ProtectedRoute permissions={["view_repair_process", "view_assigned_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiOperations />
        </ProtectedRoute>
      } />
      {/* MyCity hospitality - properties, units, reservations and stay operations */}
      <Route path="/admin/pmi-platform/hospitality" element={
        <ProtectedRoute permissions={["view_repair_process", "view_assigned_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <HospitalityOperations />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/restaurant" element={
        <ProtectedRoute permissions={["view_repair_process", "view_assigned_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiRestaurantOperations />
        </ProtectedRoute>
      } />
      {/* Compatibility redirects keep existing bookmarks while PMI has one canonical route tree. */}
      <Route path="/admin/pmi-platform" element={<Navigate to="/admin/pmi-platform/overview" replace />} />
      <Route path="/admin/pmi-platform/overview" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiOverview />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi" element={<Navigate to="/admin/pmi-platform/operations" replace />} />
      <Route path="/admin/pmi-command-center" element={<Navigate to="/admin/pmi-platform/command-center" replace />} />
      <Route path="/admin/pmi-api-connections" element={<Navigate to="/admin/pmi-platform/api-connections" replace />} />
      <Route path="/admin/hospitality" element={<Navigate to="/admin/pmi-platform/hospitality" replace />} />
      <Route path="/admin/pmi-restaurant" element={<Navigate to="/admin/pmi-platform/restaurant" replace />} />

      {/* PMI Platform — Phase-1 integrated pages */}
      <Route path="/admin/pmi-platform/dashboard" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiPlatformDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/products" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiProductStudio />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/suppliers" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiSuppliersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/orders" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiOrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/pricing" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiPricingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/widgets" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiWidgetsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/booking" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiBookingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/customers" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiCustomersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/channels" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiChannelsPage />
        </ProtectedRoute>
      } />
      {/* Merged into the unified Products workspace */}
      <Route path="/admin/pmi-platform/channel-hub" element={<Navigate to="/admin/pmi-platform/products?view=channels" replace />} />
      {/* Merged into the unified Products workspace */}
      <Route path="/admin/pmi-platform/product-master" element={<Navigate to="/admin/pmi-platform/products" replace />} />
      <Route path="/admin/pmi-platform/appointments" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiAppointmentsPage />
        </ProtectedRoute>
      } />
      {/* Merged into the unified Products workspace */}
      <Route path="/admin/pmi-platform/bom" element={<Navigate to="/admin/pmi-platform/products?tab=parts" replace />} />
      <Route path="/admin/pmi-platform/marketplace" element={
        <ProtectedRoute permissions={["manage_settings", "view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiMarketplacePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/pmi-platform/room-reservations" element={
        <ProtectedRoute permissions={["view_repair_process", "edit_repair_process"]} redirectTo="/unauthorized">
          <PmiRoomReservationsPage />
        </ProtectedRoute>
      } />


      {/* Survey Dashboard - product-advisory survey analytics */}
      <Route path="/admin/survey-dashboard" element={
        <ProtectedRoute permissions={["view_analytics", "view_dashboard"]} redirectTo="/unauthorized">
          <SurveyDashboard />
        </ProtectedRoute>
      } />

      {/* LMS Academy - Training & Certification */}
      <Route path="/lms-academy/*" element={
        <ProtectedRoute permissions={["view_lms", "manage_lms"]} redirectTo="/unauthorized">
          <LMSAcademyDashboard />
        </ProtectedRoute>
      } />

      {/* Intelligence Dashboard - Weather-Driven Demand & Operations */}
      <Route path="/intelligence" element={
        <ProtectedRoute permissions={["view_analytics", "view_dashboard"]} redirectTo="/unauthorized">
          <IntelligenceDashboard />
        </ProtectedRoute>
      } />

      {/* Repair Calendar - CEO Dashboard View */}
      <Route path="/repair-calendar" element={
        <ProtectedRoute permissions={["view_repair_process", "view_analytics", "view_dashboard"]} redirectTo="/unauthorized">
          <RepairCalendar />
        </ProtectedRoute>
      } />

      {/* System Diagnostic - Docker container monitoring & logs */}
      <Route path="/diagnostic" element={
        <ProtectedRoute permissions={["manage_settings"]} redirectTo="/unauthorized">
          <DiagnosticDashboard />
        </ProtectedRoute>
      } />

      <Route path="/communication" element={
        <ProtectedRoute permissions={["view_communication"]} redirectTo="/unauthorized">
          <CommunicationHub />
        </ProtectedRoute>
      } />

      {/* Admin-only routes */}
      <Route path="/admin/service-requests" element={
        <ProtectedRoute permissions={["view_actors", "manage_settings"]} redirectTo="/unauthorized">
          <AdminServiceRequestApproval />
        </ProtectedRoute>
      } />
      <Route path="/admin/bug-reports" element={
        <ProtectedRoute>
          <BugReportManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/booking-drafts" element={
        <ProtectedRoute permissions={["view_actors", "manage_settings"]} redirectTo="/unauthorized">
          <BookingDraftReminders />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute permissions={["view_users", "edit_users", "create_users"]} redirectTo="/unauthorized">
          <UserManagement />
        </ProtectedRoute>
      } />

      {/* Notification Management routes */}
      <Route path="/notifications" element={
        <ProtectedRoute permissions={["view_notifications", "manage_notifications"]} redirectTo="/unauthorized">
          <NotificationChannels />
        </ProtectedRoute>
      } />
      <Route path="/notifications/templates/*" element={
        <ProtectedRoute permissions={["view_notifications", "manage_notifications"]} redirectTo="/unauthorized">
          <NotificationTemplates />
        </ProtectedRoute>
      } />
      <Route path="/notifications/events" element={
        <ProtectedRoute permissions={["view_notifications", "manage_notifications"]} redirectTo="/unauthorized">
          <NotificationEventTypes />
        </ProtectedRoute>
      } />
      <Route path="/notifications/logs" element={
        <ProtectedRoute permissions={["view_notifications", "manage_notifications"]} redirectTo="/unauthorized">
          <NotificationLogs />
        </ProtectedRoute>
      } />

      {/* Supply Management routes */}
      {/* Main Supply Management Dashboard */}
      <Route path="/supply-management/*" element={
        <ProtectedRoute permissions={["view_supply", "edit_supply", "create_supply", "view_products", "create_products", "edit_products", "delete_products", "scrap_products"]} roles={["Admin", "SparePart", "Mechanics"]} redirectTo="/unauthorized">
          <SupplyManagementRouter />
        </ProtectedRoute>
      } />

      {/* Finance Management routes */}
      <Route path="/finance/*" element={
        <ProtectedRoute permissions={[
          "view_finance_dashboard", "view_finance_invoices", "view_finance_companies",
          "view_order_invoices", "view_order_repair_invoices", "view_order_webshop_orders", "view_order_finance_invoices",
          "view_finance", "edit_finance", "create_invoices", "manage_settings",
        ]} redirectTo="/unauthorized">
          <FinanceManagementRouter />
        </ProtectedRoute>
      } />

      {/* Order Management routes */}
      <Route path="/orders" element={
        <ProtectedRoute permissions={["view_supply", "edit_supply", "manage_settings"]} redirectTo="/unauthorized">
          <OrderList />
        </ProtectedRoute>
      } />
      <Route path="/orders/invoices" element={
        <ProtectedRoute permissions={[
          "view_order_invoices", "view_order_repair_invoices", "view_order_webshop_orders", "view_order_finance_invoices",
          "view_supply", "edit_supply", "manage_settings",
        ]} redirectTo="/unauthorized">
          <OrderInvoiceList />
        </ProtectedRoute>
      } />
      <Route path="/invoices/:id/edit" element={
        <ProtectedRoute permissions={["view_supply", "edit_supply", "manage_settings"]} redirectTo="/unauthorized">
          <InvoiceEditPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/:id" element={
        <ProtectedRoute permissions={["view_supply", "edit_supply", "manage_settings"]} redirectTo="/unauthorized">
          <OrderDetail />
        </ProtectedRoute>
      } />
      <Route path="/orders/:id/edit" element={
        <ProtectedRoute permissions={["view_supply", "edit_supply", "manage_settings"]} redirectTo="/unauthorized">
          <OrderEdit />
        </ProtectedRoute>
      } />

      {/* Redirect any unknown authenticated routes to repair processes */}
      <Route path="*" element={<Navigate to="/repair-processes" replace />} />
    </Routes>
  );
};

// Component to handle authentication check and redirect
const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();

  // If still loading auth state, show nothing (or could show a loading spinner)
  if (loading) {
    return <div className="d-flex justify-content-center p-5"><div className="spinner-border text-success" role="status"></div></div>;
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated, render children
  return children;
};

RequireAuth.propTypes = {
  children: PropTypes.node.isRequired,
};

export default App;

